import { useCallback, useRef, useState } from "react";
import HistoricAPI from "../../apis/display-layers/historicFiresAPI.js";
import { initialHistoric, emptyPolygon } from "../../conts/defaultValues.js";
import { featureFromCollection } from "../../utils/polygonImport";

const HISTORIC_COUNTRY = "france";
const HISTORIC_DEPARTMENTS = ["var"];

// setRealPolygon comes from useAnalysisWorkflow: the selected fire becomes the sinistré polygon
const useHistoricFires = (setRealPolygon) => {
  const historicApi = useRef(new HistoricAPI()).current;
  const polygonRequest = useRef(0); // ignores late responses
  const [historicFires, setHistoricFires] = useState(initialHistoric);

  // list of historic fires
  const fetchHistoricFires = useCallback(async () => {
    setHistoricFires((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await historicApi.getAllHistoricFiresApi({
        country: HISTORIC_COUNTRY,
        departments: HISTORIC_DEPARTMENTS,
      });
      if (response.status !== 200) throw new Error("Unexpected status");
      setHistoricFires({
        data: response.data.data ?? [],
        isLoading: false,
        error: null,
      });
    } catch (err) {
      console.error("Historic fires error:", err);
      setHistoricFires((prev) => ({
        ...prev,
        isLoading: false,
        error: "Impossible de charger les feux historiques.",
      }));
    }
  }, []);

  // selected fire -> stored directly as the sinistré polygon
  const selectHistoricFire = useCallback(
    async (fire) => {
      const requestId = ++polygonRequest.current;
      try {
        const response = await historicApi.getHistoricFirePolygonsApi({
          ids: [fire.id], // ADJUST if the API expects another field (uuid...)
        });
        if (requestId !== polygonRequest.current) return null;
        if (response.status !== 200) throw new Error("Unexpected status");

        const { feature, area } = featureFromCollection(response.data.data?.[0]);
        setRealPolygon({ ...emptyPolygon, feature, area, fireId: fire.id });
        return feature; // lets the template zoom to it
      } catch (err) {
        console.error("Historic polygon error:", err);
        return null;
      }
    },
    [setRealPolygon],
  );

  // called on reset: a polygon still loading will be ignored
  const cancelSelection = useCallback(() => {
    polygonRequest.current++;
  }, []);

  return { historicFires, fetchHistoricFires, selectHistoricFire, cancelSelection };
};

export default useHistoricFires;
